import { useConversations } from "@coaching-culture/api-client";
import {
  Button,
  CheckBox,
  DateRangeButton,
  DateRangePreset,
  EmptyState,
  Loader,
  Select,
  typeSize,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { PageHeader } from "components/PageHeader";
import {
  addDays,
  addMonths,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  fromUnixTime,
  startOfDay,
  startOfMonth,
  startOfToday,
} from "date-fns";
import { sortBy } from "lodash";
import { useTeam } from "queries/performance";
import { useMyRelationships } from "queries/team";
import { useMemo, useState } from "react";
import { FaDownload, FaPlusCircle, FaRegCalendar } from "react-icons/fa";
import styled from "styled-components";
import { saveExportAsCsv } from "utils";
import { useConversationActions } from "./ConversationActions";
import { buildConversationHistoryRows } from "./conversationHistoryExport";
import {
  ConvoAndInstance,
  groupPast,
  groupUpcoming,
  isCancelled,
  isUpcoming,
  needsCompleting,
  playHref,
  toInstances,
} from "./conversationList";
import { ConversationRow } from "./ConversationRow";
import { WeekView } from "./WeekView";

type View = "list" | "week";
type Tab = "upcoming" | "past";

const NEEDS_COMPLETING_PREVIEW = 3;

const readSession = (key: string, fallback: string) => {
  try {
    return sessionStorage.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
};

const writeSession = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Not available (private window etc.); the choice just isn't remembered.
  }
};

const upcomingPresets: DateRangePreset[] = [
  {
    label: "This week",
    getRange: () => [startOfToday(), endOfWeek(new Date(), { weekStartsOn: 1 })],
  },
  {
    label: "Next 30 days",
    getRange: () => [startOfToday(), endOfDay(addDays(new Date(), 30))],
  },
  {
    label: "Next 3 months",
    getRange: () => [startOfToday(), endOfDay(addMonths(new Date(), 3))],
  },
  {
    label: "This month",
    getRange: () => [startOfMonth(new Date()), endOfMonth(new Date())],
  },
];

const pastPresets: DateRangePreset[] = [
  {
    label: "Last 30 days",
    getRange: () => [startOfDay(addDays(new Date(), -30)), endOfDay(new Date())],
  },
  {
    label: "Last 3 months",
    getRange: () => [
      startOfDay(addMonths(new Date(), -3)),
      endOfDay(new Date()),
    ],
  },
  {
    label: "Last 12 months",
    getRange: () => [
      startOfDay(addMonths(new Date(), -12)),
      endOfDay(new Date()),
    ],
  },
  {
    label: "This month",
    getRange: () => [startOfMonth(new Date()), endOfMonth(new Date())],
  },
];

const Toolbar = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px;
  margin-bottom: 24px;
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;

  & > label {
    font-size: ${typeSize("small")};
    color: ${(p) => p.theme.colors.textMuted};
  }
`;

const Segmented = styled.div`
  display: flex;
  padding: 3px;
  border-radius: 10px;
  background: ${(p) => p.theme.colors.grey5};
`;

const Segment = styled.button<{ active: boolean }>`
  min-height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 0;
  font-family: inherit;
  font-size: ${typeSize("small")};
  font-weight: ${(p) => (p.active ? 600 : 500)};
  cursor: pointer;
  background: ${(p) => (p.active ? p.theme.colors.surface : "transparent")};
  color: ${(p) => (p.active ? p.theme.colors.black : p.theme.colors.body)};
  box-shadow: ${(p) =>
    p.active ? "0 1px 2px rgba(38, 36, 31, 0.12)" : "none"};

  &:focus-visible {
    outline: 2px solid ${(p) => p.theme.colors.primary};
    outline-offset: 2px;
  }
`;

const ToolbarEnd = styled.div`
  margin-left: auto;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const TodoPanel = styled.section`
  background: ${(p) => p.theme.colors.warningSoft};
  border-radius: 12px;
  padding: 18px 20px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  & > div:first-child {
    display: flex;
    gap: 12px;
    align-items: baseline;
    flex-wrap: wrap;
  }

  & h2 {
    margin: 0;
    flex: 1;
    font-size: ${typeSize("title")};
    font-weight: 700;
    color: ${(p) => p.theme.colors.black};
  }

  & > p {
    margin: -6px 0 0;
    font-size: ${typeSize("small")};
    color: ${(p) => p.theme.colors.warningText};
  }
`;

const TodoList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  background: ${(p) => p.theme.colors.surface};
  border-radius: 10px;

  & > li {
    display: flex;
    gap: 14px;
    align-items: center;
    flex-wrap: wrap;
    padding: 12px 16px;
    border-bottom: 1px solid ${(p) => p.theme.colors.grey5};
    font-size: ${typeSize("body")};
    color: ${(p) => p.theme.colors.black};
  }

  & > li:last-child {
    border-bottom: 0;
  }

  & > li > span:first-child {
    width: 96px;
    font-size: ${typeSize("small")};
    font-weight: 600;
  }

  & > li > span:nth-child(2) {
    flex: 1;
    min-width: 200px;
  }
`;

const Group = styled.section`
  margin-bottom: 24px;

  & > h2 {
    margin: 0 0 8px;
    font-size: ${typeSize("small")};
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${(p) => p.theme.colors.textMuted};
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
`;

const EmptyPanel = styled.div`
  background: ${(p) => p.theme.colors.surface};
  border: 1px solid ${(p) => p.theme.colors.border};
  border-radius: 12px;
`;

const NeedsCompletingRow = ({ convo }: { convo: ConvoAndInstance }) => {
  const [user] = useUser();
  const actions = useConversationActions(convo);
  const other =
    convo.manager.id === user.id ? convo.directReport : convo.manager;

  return (
    <li>
      {actions.modals}
      <span>{format(fromUnixTime(convo.scheduledDate), "EEE d MMM")}</span>
      <span>
        <strong>{convo.type}</strong> with {other.name}
      </span>
      {actions.canCancel && (
        <Button small variant="ghost" onClick={actions.startCancel}>
          Cancel it
        </Button>
      )}
      <Button small to={playHref(convo)}>
        Complete
      </Button>
    </li>
  );
};

export function Conversations() {
  const [user] = useUser();
  const { data: team, isFetched: teamFetched } = useTeam();
  const { data: convos, isFetched } = useConversations();
  const { data: relationships } = useMyRelationships();

  const [view, setView] = useState<View>(
    () => (readSession("conversationsView", "list") as View) ?? "list",
  );
  const [tab, setTab] = useState<Tab>("upcoming");
  const [showCancelled, setShowCancelled] = useState(false);
  const [showAllTodo, setShowAllTodo] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(() =>
    readSession("selectedTeam", ""),
  );
  const [upcomingRange, setUpcomingRange] = useState<[Date, Date]>(() => [
    startOfToday(),
    endOfDay(addMonths(new Date(), 3)),
  ]);
  const [pastRange, setPastRange] = useState<[Date, Date]>(() => [
    startOfDay(addMonths(new Date(), -3)),
    endOfDay(new Date()),
  ]);

  const pickView = (v: View) => {
    setView(v);
    writeSession("conversationsView", v);
  };

  const exportHistory = () => {
    const rows = buildConversationHistoryRows(convos ?? [], user.id);
    saveExportAsCsv("ConversationHistory.csv")(rows);
  };

  const isManager = relationships?.some((r) => r.type === "manager");

  // Everything for the chosen person, before the date range and cancelled
  // filters (the week view and the needs-completing panel ignore the range).
  const forPerson = useMemo(() => {
    const all = toInstances(convos ?? []);
    return selectedTeam === ""
      ? all
      : all.filter(
          (x) =>
            x.directReport.id === selectedTeam ||
            x.manager.id === selectedTeam,
        );
  }, [convos, selectedTeam]);

  const visible = showCancelled
    ? forPerson
    : forPerson.filter((x) => !isCancelled(x));

  const inRange = (x: ConvoAndInstance, [start, end]: [Date, Date]) => {
    const d = fromUnixTime(x.scheduledDate);
    return d >= startOfDay(start) && d <= endOfDay(end);
  };

  const upcoming = visible.filter(
    (x) => isUpcoming(x) && inRange(x, upcomingRange),
  );
  const past = visible.filter(
    (x) => !isUpcoming(x) && inRange(x, pastRange),
  );

  const todo = sortBy(
    forPerson.filter((x) => needsCompleting(x) && x.manager.id === user.id),
    (x) => -x.scheduledDate,
  );
  const todoShown = showAllTodo
    ? todo
    : todo.slice(0, NEEDS_COMPLETING_PREVIEW);

  const personOptions = [
    { label: "Everyone", value: "" },
    ...(team?.directReports ?? []).map((u) => ({ label: u.name, value: u.id })),
    ...(team?.lineManagers ?? []).map((u) => ({ label: u.name, value: u.id })),
  ];

  const groups = tab === "upcoming" ? groupUpcoming(upcoming) : groupPast(past);
  const range = tab === "upcoming" ? upcomingRange : pastRange;

  return (
    <>
      <PageHeader
        text="Conversations"
        subtitle="Your coaching conversations, past and upcoming"
      >
        {isManager && (
          <Button
            to="/solutions/performance/conversations/create"
            variant="primary"
            icon={FaPlusCircle}
          >
            Schedule conversation
          </Button>
        )}
      </PageHeader>

      <Toolbar>
        <Segmented aria-label="View">
          <Segment
            type="button"
            active={view === "list"}
            aria-pressed={view === "list"}
            onClick={() => pickView("list")}
          >
            List
          </Segment>
          <Segment
            type="button"
            active={view === "week"}
            aria-pressed={view === "week"}
            onClick={() => pickView("week")}
          >
            Week
          </Segment>
        </Segmented>
        {view === "list" && (
          <Segmented aria-label="Which conversations">
            <Segment
              type="button"
              active={tab === "upcoming"}
              aria-pressed={tab === "upcoming"}
              onClick={() => setTab("upcoming")}
            >
              Upcoming · {upcoming.length}
            </Segment>
            <Segment
              type="button"
              active={tab === "past"}
              aria-pressed={tab === "past"}
              onClick={() => setTab("past")}
            >
              Past · {past.length}
            </Segment>
          </Segmented>
        )}
        {teamFetched && personOptions.length > 1 && (
          <>
            <label htmlFor="conversations-person">With</label>
            <Select
              id="conversations-person"
              width={200}
              options={personOptions}
              value={selectedTeam}
              onChange={(ev) => {
                setSelectedTeam(ev.target.value);
                writeSession("selectedTeam", ev.target.value);
              }}
            />
          </>
        )}
        {view === "list" && (
          <DateRangeButton
            value={range}
            onChange={(r: [Date, Date]) =>
              tab === "upcoming" ? setUpcomingRange(r) : setPastRange(r)
            }
            noNull
            presets={tab === "upcoming" ? upcomingPresets : pastPresets}
          />
        )}
        <ToolbarEnd>
          <CheckBox
            mb={0}
            label="Show cancelled"
            value={showCancelled}
            onChange={setShowCancelled}
          />
          <Button
            small
            variant="ghost"
            icon={FaDownload}
            disabled={!isFetched}
            onClick={exportHistory}
          >
            Export history
          </Button>
        </ToolbarEnd>
      </Toolbar>

      {!isFetched ? (
        <Loader />
      ) : (
        <>
          {todo.length > 0 && (
            <TodoPanel aria-labelledby="conversations-todo">
              <div>
                <h2 id="conversations-todo">
                  {todo.length === 1
                    ? "1 past conversation isn't marked complete"
                    : `${todo.length} past conversations aren't marked complete`}
                </h2>
                {todo.length > NEEDS_COMPLETING_PREVIEW && (
                  <Button
                    small
                    variant="ghost"
                    onClick={() => setShowAllTodo((x) => !x)}
                  >
                    {showAllTodo ? "Show fewer" : `Show all ${todo.length}`}
                  </Button>
                )}
              </div>
              <p>
                Complete them to record notes and action items, or cancel ones
                that didn't happen.
              </p>
              <TodoList>
                {todoShown.map((x) => (
                  <NeedsCompletingRow key={`${x.id}-${x.index}`} convo={x} />
                ))}
              </TodoList>
            </TodoPanel>
          )}

          {view === "week" ? (
            <WeekView instances={visible} />
          ) : groups.length === 0 ? (
            <EmptyPanel>
              <EmptyState
                icon={FaRegCalendar}
                title={
                  tab === "upcoming" ? "Nothing scheduled" : "No past conversations"
                }
                action={
                  isManager && tab === "upcoming" ? (
                    <Button
                      to="/solutions/performance/conversations/create"
                      icon={FaPlusCircle}
                    >
                      Schedule conversation
                    </Button>
                  ) : undefined
                }
              >
                There are no conversations in this date range.
              </EmptyState>
            </EmptyPanel>
          ) : (
            groups.map((g) => (
              <Group key={g.label} aria-label={g.label}>
                <h2>{g.label}</h2>
                <List>
                  {g.items.map((x) => (
                    <ConversationRow key={`${x.id}-${x.index}`} convo={x} />
                  ))}
                </List>
              </Group>
            ))
          )}
        </>
      )}
    </>
  );
}
